import {
  type QueryClient,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import type { AxiosInstance } from "axios";
import { useApi } from "@/core/api";
import type {
  EnrichedPayPeriod,
  IPayPeriod,
  IPayPeriodDetail,
} from "@/payroll/payroll.d";
import { payPeriodDetailPeriodKey, payPeriodsKey } from "./query";

const mergePayPeriodUpdate = <TPeriod extends IPayPeriod>(
  period: TPeriod,
  updatedPeriod: IPayPeriod,
): TPeriod =>
  period.id === updatedPeriod.id
    ? ({ ...period, ...updatedPeriod } as TPeriod)
    : period;

const invalidatePayPeriodState = (
  queryClient: QueryClient,
  businessId: string | null,
  periodId: string | null,
) =>
  Promise.all([
    queryClient.invalidateQueries({ queryKey: payPeriodsKey(businessId) }),
    queryClient.invalidateQueries({
      queryKey: payPeriodDetailPeriodKey(businessId, periodId),
    }),
  ]);

export const getLockPayPeriodMutationOptions = ({
  api,
  queryClient,
  businessId,
  periodId,
}: {
  api: AxiosInstance;
  queryClient: QueryClient;
  businessId: string | null;
  periodId: string | null;
}) => ({
  mutationFn: (): Promise<IPayPeriod> =>
    api
      .patch(`/businesses/${businessId}/pay_periods/${periodId}/lock`)
      .then((response) => response.data),
  onSuccess: (updatedPeriod: IPayPeriod) => {
    queryClient.setQueryData<EnrichedPayPeriod[]>(
      payPeriodsKey(businessId),
      (periods) =>
        periods?.map((period) => mergePayPeriodUpdate(period, updatedPeriod)),
    );
    queryClient.setQueriesData<IPayPeriodDetail>(
      { queryKey: payPeriodDetailPeriodKey(businessId, periodId) },
      (period) =>
        period ? mergePayPeriodUpdate(period, updatedPeriod) : period,
    );
  },
  // Deliberately not returned. React Query holds the caller's own onError until
  // a returned promise settles, and the refetch this starts unmounts the lock
  // dialog first — which silently drops the toast that would have said why the
  // lock did not take.
  onError: () => {
    void invalidatePayPeriodState(queryClient, businessId, periodId);
  },
});

export const useLockPayPeriod = (
  businessId: string | null,
  periodId: string | null,
) => {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation(
    getLockPayPeriodMutationOptions({
      api,
      queryClient,
      businessId,
      periodId,
    }),
  );
};
